// Caller-journey writes on a Call Logs row (contracts ~/pm/contracts/B4.md v1.2 §4–6,
// B3.md §3). Every result is one of:
//   { ok: true, data }       saved
//   { uncertain: true }      no response or a 5xx: it may have saved, never say "failed"
//   { ok: false, message }   refused (403 → ask an admin)
import { callApi } from '../../../../utils/apiCall';
import newIdempotencyKey from '../../../../utils/idempotencyKey';
import { callsBase } from '../callLogQuery';

const rowUrl = (practiceId, callId, tail) => `${callsBase(practiceId)}${encodeURIComponent(callId)}/${tail}`;
export const linkUrl = (practiceId, callId) => rowUrl(practiceId, callId, 'link/');
export const notAPatientUrl = (practiceId, callId) => rowUrl(practiceId, callId, 'not-a-patient/');
export const undoFlagUrl = (practiceId, flagId) => `${callsBase(practiceId)}number-flags/${encodeURIComponent(flagId)}/undo/`;
export const leadPrefillUrl = (practiceId, callId) => rowUrl(practiceId, callId, 'lead-prefill/');
// B3 addresses the voice call by its number (`voice:123` → 123).
export const askAdminUrl = (practiceId, callId) => `/api/v1/practices/${practiceId}/voice-calls/${String(callId).split(':').pop()}/ask-admin/`;

const PERMISSION_MESSAGE = "You don't have permission to do this. Ask an admin.";
const MESSAGES = { undo_expired: 'The 30-day undo window has passed.' };

const messageOf = (response, fallback) => {
  if (response.status === 403) return PERMISSION_MESSAGE;
  const data = response.data || {};
  if (MESSAGES[data.code]) return MESSAGES[data.code];
  const first = (v) => (Array.isArray(v) ? v[0] : v);
  const detail = data.detail || first(data) || first(data.lead_id) || first(data.label) || first(data.non_field_errors);
  return typeof detail === 'string' ? detail : fallback;
};

export const resultOf = (response, fallback = 'This could not be saved.') => {
  if (response?.ok) return { ok: true, data: response.data };
  if (!response || response.status === 0 || response.status >= 500) return { ok: false, uncertain: true };
  return { ok: false, status: response.status, message: messageOf(response, fallback) };
};

// The dialogs own error text (a 5xx is "unknown", never "failed"), and link with
// unarchive runs the full lead restore, so give writes 30 s instead of the default 10.
const WRITE_OPTIONS = { suppressServerErrorDispatch: true, timeout: 30000 };
const post = (url, body, key) => callApi(url, 'POST', body, false, { ...WRITE_OPTIONS, headers: { 'Idempotency-Key': key || newIdempotencyKey() } });

// Link this call (and, by default, earlier unlinked calls from the number) to a lead.
// `leadId: null` = "Not them": unlink and stop auto-matching this call.
export async function linkCall(practiceId, row, { leadId, unarchive = false, includeEarlier = true }, key) {
  return resultOf(await post(linkUrl(practiceId, row.id), { lead_id: leadId, unarchive, include_earlier: includeEarlier }, key), 'The call could not be linked.');
}

export async function markNotAPatient(practiceId, row, label, key) {
  return resultOf(await post(notAPatientUrl(practiceId, row.id), label ? { label } : {}, key), 'The number could not be marked.');
}

export async function undoNotAPatient(practiceId, flagId, key) {
  return resultOf(await post(undoFlagUrl(practiceId, flagId), {}, key), 'The flag could not be undone.');
}

export async function askAdmin(practiceId, row, key) {
  // Only phone-system (voice:) calls have lines to set up.
  if (!String(row?.id || '').startsWith('voice:')) return { ok: false, message: 'Only phone-system calls can be sent to an admin.' };
  return resultOf(await post(askAdminUrl(practiceId, row.id), {}, key), 'Admins could not be notified.');
}

// `quiet`: a request the user didn't make (the prefetch) never shows "Something went wrong".
const requestLeadPrefill = async (practiceId, row, { quiet = false } = {}) => {
  const response = quiet
    ? await callApi(leadPrefillUrl(practiceId, row.id), 'GET', null, false, { suppressServerErrorDispatch: true })
    : await callApi(leadPrefillUrl(practiceId, row.id));
  return response?.ok && response.data && typeof response.data === 'object' ? response.data : null;
};

// The call pill asks for a ringing call's pre-fill early, so its Create lead opens at once
// (CU-869fmcc9r). The next fetchLeadPrefill for that row takes the answer (or the request
// still on its way) once, even an empty one (the call fills every gap); a stale one is asked
// again. `refresh` asks again even when a fresh answer is held (the call moved on).
const PREFILL_FRESH_MS = 2 * 60 * 1000;
const prefetched = new Map();
const prefillKey = (practiceId, row) => `${practiceId}:${row?.id}`;

export function prefetchLeadPrefill(practiceId, row, { refresh = false } = {}) {
  if (!practiceId || !row?.id) return;
  const key = prefillKey(practiceId, row);
  const hit = prefetched.get(key);
  if (!refresh && hit && Date.now() - hit.at < PREFILL_FRESH_MS) return;
  prefetched.set(key, { at: Date.now(), answer: requestLeadPrefill(practiceId, row, { quiet: true }).catch(() => null) });
}

// GET; null when the backend doesn't have the endpoint yet or refuses.
export async function fetchLeadPrefill(practiceId, row) {
  const key = prefillKey(practiceId, row);
  const hit = prefetched.get(key);
  prefetched.delete(key);
  if (hit && Date.now() - hit.at < PREFILL_FRESH_MS) return hit.answer;
  return requestLeadPrefill(practiceId, row);
}
